import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Timeline,
  Typography,
  message,
} from 'antd';
import type { TimelineProps } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { ThunderboltOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Importación actualizada: API movida a features/erp/subscriptions/api
import { subscriptionsApi } from '../api/subscriptions.ts';
import type {
  SubscriptionAvailabilityEntry,
  SubscriptionUsageSummary,
  UsageConsumePayload,
  UsageHistoryResponse,
} from '@app-types/subscriptions.ts';

const { Text } = Typography;

const COLORS = ['#1890ff', '#13c2c2', '#52c41a', '#722ed1', '#fa8c16', '#eb2f96'];

interface UsageDashboardProps {
  subscriptionId: number;
  currentCycleId?: number;
}

interface ConsumeFormValues {
  item_id: number;
  quantity: number;
  notes?: string;
}

const UsageDashboard = ({ subscriptionId, currentCycleId }: UsageDashboardProps): JSX.Element => {
  const [showConsume, setShowConsume] = useState(false);
  const [form] = Form.useForm<ConsumeFormValues>();
  const queryClient = useQueryClient();

  const summaryKey = ['usage-summary', subscriptionId, currentCycleId];
  const availabilityKey = ['usage-available', subscriptionId];
  const historyKey = ['usage-history', subscriptionId, currentCycleId];

  const summaryQuery = useQuery<SubscriptionUsageSummary[]>({
    queryKey: summaryKey,
    queryFn: () => subscriptionsApi.getUsageSummary(subscriptionId, currentCycleId),
    refetchInterval: 30_000,
  });

  const availabilityQuery = useQuery<SubscriptionAvailabilityEntry[]>({
    queryKey: availabilityKey,
    queryFn: () => subscriptionsApi.getAvailableCredits(subscriptionId),
  });

  const historyQuery = useQuery<UsageHistoryResponse>({
    queryKey: historyKey,
    queryFn: () =>
      subscriptionsApi.getUsageHistory(subscriptionId, { cycle_id: currentCycleId, limit: 20 }),
    refetchInterval: 30_000,
  });

  const consumeMutation = useMutation({
    mutationFn: (payload: UsageConsumePayload) => subscriptionsApi.consumeCredits(subscriptionId, payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: summaryKey });
      await queryClient.cancelQueries({ queryKey: availabilityKey });

      const previousSummary = queryClient.getQueryData<SubscriptionUsageSummary[]>(summaryKey);
      const previousAvailability = queryClient.getQueryData<SubscriptionAvailabilityEntry[]>(availabilityKey);

      if (previousSummary) {
        const updated = previousSummary.map((entry) =>
          entry.item_id === payload.item_id
            ? {
                ...entry,
                total_consumed: Number(entry.total_consumed) + payload.quantity,
                net_consumed: Number(entry.net_consumed) + payload.quantity,
              }
            : entry,
        );
        queryClient.setQueryData(summaryKey, updated);
      }

      if (previousAvailability) {
        const updatedAvailability = previousAvailability.map((entry) =>
          entry.item_id === payload.item_id && !entry.unlimited
            ? { ...entry, available: Math.max(0, Number(entry.available ?? 0) - payload.quantity) }
            : entry,
        );
        queryClient.setQueryData(availabilityKey, updatedAvailability);
      }

      return { previousSummary, previousAvailability };
    },
    onError: (_err, _payload, context) => {
      if (context?.previousSummary) {
        queryClient.setQueryData(summaryKey, context.previousSummary);
      }
      if (context?.previousAvailability) {
        queryClient.setQueryData(availabilityKey, context.previousAvailability);
      }
      message.error('No pudimos registrar el consumo');
    },
    onSuccess: () => {
      message.success('Consumo registrado');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: summaryKey });
      queryClient.invalidateQueries({ queryKey: availabilityKey });
      queryClient.invalidateQueries({ queryKey: historyKey });
      setShowConsume(false);
    },
  });

  const summary = summaryQuery.data ?? [];
  const availability = availabilityQuery.data ?? [];
  const history = historyQuery.data?.items ?? [];

  const totals = useMemo(() => {
    const totalConsumed = summary.reduce((acc, entry) => acc + Number(entry.net_consumed), 0);
    const availableCredits = availability
      .filter((entry) => !entry.unlimited)
      .reduce((acc, entry) => acc + Number(entry.available ?? 0), 0);
    const percent = availableCredits > 0 ? Math.min(100, (totalConsumed / availableCredits) * 100) : 0;
    return { totalConsumed, availableCredits, percent: Number(percent.toFixed(1)) };
  }, [availability, summary]);

  const availableOptions = useMemo(() => {
    const labelMap = new Map<number, string>();
    summary.forEach((entry) => labelMap.set(entry.item_id, entry.item_name));
    return availability.map((entry) => ({
      value: entry.item_id,
      label: labelMap.get(entry.item_id) ?? `Item ${entry.item_id}`,
      disabled: !entry.unlimited && Number(entry.available ?? 0) <= 0,
    }));
  }, [availability, summary]);

  const timelineItems: TimelineProps['items'] = history.map((usage) => ({
    key: usage.id,
    color: usage.source === 'refund' ? 'green' : 'red',
    children: (
      <Space orientation="vertical" size={0}>
        <Text strong>
          {usage.source.toUpperCase()} · {usage.quantity}
        </Text>
        <Text type="secondary">{usage.notes ?? 'Sin notas'} · {usage.created_at}</Text>
      </Space>
    ),
  }));

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Total consumido" value={totals.totalConsumed} prefix={<ThunderboltOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Créditos disponibles" value={totals.availableCredits} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Text>% utilizado</Text>
              <Progress percent={totals.percent} status="active" />
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Card title="Consumo por servicio" size="small" bodyStyle={{ height: 260 }}>
            {summary.length === 0 ? (
              <Text type="secondary">Sin consumos registrados</Text>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie dataKey="net_consumed" data={summary} nameKey="item_name" label>
                    {summary.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Historial (últimos 20)" size="small">
            <Timeline items={timelineItems} pending={historyQuery.isFetching ? 'Actualizando...' : undefined} />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Detalle de créditos">
        <Space orientation="vertical" style={{ width: '100%' }}>
          {availability.length === 0 ? (
            <Text type="secondary">Sin datos de disponibilidad</Text>
          ) : (
            availability.map((entry) => (
              <Row key={entry.item_id} justify="space-between" align="middle">
                <Col>
                  <Text strong>Item {entry.item_id}</Text>
                </Col>
                <Col>
                  {entry.unlimited ? (
                    <Text type="success">Ilimitado</Text>
                  ) : (
                    <Text>{entry.available ?? 0} disponibles</Text>
                  )}
                </Col>
              </Row>
            ))
          )}
        </Space>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowConsume(true)}>
          Consumir manualmente
        </Button>
      </div>

      <Modal
        open={showConsume}
        title="Registrar consumo"
        onCancel={() => setShowConsume(false)}
        footer={null}
      >
        <Form<ConsumeFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) =>
            consumeMutation.mutate({
              item_id: values.item_id,
              quantity: values.quantity,
              cycle_id: currentCycleId,
              notes: values.notes,
            })
          }
        >
          <Form.Item
            label="Ítem"
            name="item_id"
            rules={[{ required: true, message: 'Seleccioná un ítem' }]}
          >
            <Select options={availableOptions} placeholder="Seleccioná un ítem" />
          </Form.Item>
          <Form.Item
            label="Cantidad"
            name="quantity"
            rules={[
              { required: true, message: 'Ingresá una cantidad' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null) return Promise.resolve();
                  const itemId = form.getFieldValue('item_id');
                  const entry = availability.find((item) => item.item_id === itemId);
                  if (entry && !entry.unlimited && value > Number(entry.available ?? 0)) {
                    return Promise.reject(new Error('No hay créditos suficientes'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber min={0.1} style={{ width: '100%' }} step={0.1} />
          </Form.Item>
          <Form.Item label="Notas" name="notes">
            <Input.TextArea rows={3} maxLength={255} />
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowConsume(false)}>Cerrar</Button>
            <Button type="primary" htmlType="submit" loading={consumeMutation.isPending}>
              Registrar
            </Button>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
};

export default UsageDashboard;
